import config from '@/config'
import { Platform } from '@/helper/const'
import { UploadMediaResponse } from '@/types'
import { Capacitor } from '@capacitor/core'
import { v4 as uuidv4 } from 'uuid'
import { requests } from './HTTPService'

const http = requests(config?.COMMUNITIES_SERVER_URL)

export default class MediaService {
  protected basePath = ''
  private locationId = ''
  private groupId = ''

  constructor(locationId: string, groupId: string) {
    this.locationId = locationId
    this.basePath = `${locationId}/medias`
    this.groupId = groupId
  }

  async generateSignedUrl(data: any): Promise<any> {
    return http
      .post(`${this.basePath}/signed-url`, data, {
        params: { groupId: this.groupId },
      })
      .then(res => res.data)
  }

  async generateSettingsSignedUrl(data: any): Promise<any> {
    return http.post(`${this.basePath}/signed-url`, data).then(res => res.data)
  }

  async uploadFile(payload: any): Promise<{
    uploadPromise: Promise<UploadMediaResponse>
    cancelUpload: Function
  }> {
    const name: string = payload.filename || uuidv4()
    const folder: string = payload.folder || 'other'

    const { signedUrl, url } = await this.generateSignedUrl({
      name,
      folder,
      type: payload.type,
    })

    const blobedFile = new File([payload.file], name)

    const xhr = new XMLHttpRequest()

    const uploadPromise: Promise<UploadMediaResponse> = new Promise(function (
      resolve,
      reject
    ) {
      const formData = new FormData()
      formData.append('file', blobedFile)
      xhr.open('PUT', signedUrl, true)
      xhr.onload = () => {
        const status = xhr.status
        if (status === 200) {
          resolve({
            id: payload.id,
            url: url,
            name: name,
            type: payload.type,
            size: payload.size,
            status: 'completed',
          })
        } else {
          reject('Something went wrong')
        }
      }
      xhr.onerror = () => {
        reject('Something went wrong')
      }
      xhr.setRequestHeader('Content-Type', payload.type)
      if (payload.progressUpdate) {
        if (
          Capacitor.getPlatform() === Platform.IOS ||
          Capacitor.getPlatform() === Platform.Android
        ) {
          // Fake progress for iOS and Android
          let progress = 0
          const progressInterval = setInterval(() => {
            progress += 20
            if (progress > 90) {
              progress = 90
              clearInterval(progressInterval)
            }
            payload.progressUpdate(progress)
          }, 500)
        } else {
          xhr.upload.onprogress = function (event) {
            if (event.lengthComputable) {
              const percentComplete = Number(
                ((event.loaded / event.total) * 100).toFixed()
              )
              payload.progressUpdate(percentComplete)
            }
          }
        }
      }
      xhr.onloadend = function () {
        if (payload.progressUpdate) {
          // Set progress to 100 when upload is complete
          payload.progressUpdate(100)
        }
      }
      xhr.send(blobedFile)
    })

    const cancelUpload = () => {
      xhr.abort()
    }

    return { uploadPromise, cancelUpload }
  }
}
